import type { ClientOrganizationProfile } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetOwnOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOwnOrganizations';
import { useGetPerson } from '@/features/api/hooks/participant/useGetPerson';
import { ListItem } from '@/features/list/components/ListItem';
import { BottomModal } from '@/features/popup/components/BottomModal';
import {
  largeOrganizationLogoIcon,
  personAvatarIcon,
  selectedIcon
} from '@/features/sender-selection/components/senderSelectionIcons';
import { useLastSenderStore } from '@/features/sender-selection/store/useLastSenderStore';
import { SenderType } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { hasItems } from '@/shared/utils/array';
import { getPublicName } from '@/shared/utils/string';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  organizations?: ClientOrganizationProfile[];
  showPersonalProfile?: boolean;
  onSenderSelect?: () => void;
};

export const SenderSelectionBottomSheet: FC<Props> = ({
  isVisible,
  onClose,
  organizations,
  showPersonalProfile = true,
  onSenderSelect
}) => {
  const { t } = useTranslation(LocalizationNameSpace.SENDER_SELECTION, {
    keyPrefix: 'selectionSheet'
  });
  const { t: tTags } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.tagLabels'
  });

  const sender = useLastSenderStore((state) => state.sender);
  const setSender = useLastSenderStore((state) => state.setSender);
  const { data: person } = useGetPerson();
  const { data: fallbackOrganizations } = useGetOwnOrganizations();

  const resolvedOrganizations = organizations ?? fallbackOrganizations ?? [];

  const selectUser = () => {
    onSenderSelect?.();
    setSender({ type: SenderType.USER });
    onClose();
  };

  const selectOrganization = (organizationId: string) => {
    onSenderSelect?.();
    setSender({ type: SenderType.ORGANIZATION, organizationId });
    onClose();
  };

  return (
    <BottomModal
      isVisible={isVisible}
      title={t('title')}
      titleAlign="left"
      onClose={onClose}
    >
      {showPersonalProfile && (
        <ListItem
          testID="sender-option-user"
          title={getPublicName(person?.firstName, person?.lastName) || '…'}
          description={t('personalProfile')}
          icon={personAvatarIcon(person)}
          onPress={selectUser}
          {...selectedIcon(sender.type === SenderType.USER)}
        />
      )}
      {hasItems(resolvedOrganizations) &&
        resolvedOrganizations.map((organization) => {
          const primaryTag = organization.tags?.[0];
          return (
            <ListItem
              key={organization.id}
              testID={`sender-option-org-${organization.id}`}
              title={organization.name}
              description={primaryTag ? tTags(primaryTag) : undefined}
              icon={largeOrganizationLogoIcon(organization.logoImage)}
              onPress={() => selectOrganization(organization.id)}
              {...selectedIcon(
                sender.type === SenderType.ORGANIZATION &&
                  sender.organizationId === organization.id
              )}
            />
          );
        })}
    </BottomModal>
  );
};
